import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SyncIcon from "@mui/icons-material/Sync";
import { useTranslation } from "react-i18next";
import { postPlatformPassengerFeesBackfill } from "../../../api/passagensApi";

const STATUS_ICON = {
  pending: <HourglassEmptyIcon fontSize="small" color="disabled" />,
  running: <CircularProgress size={18} />,
  ok: <CheckCircleIcon fontSize="small" color="success" />,
  partial: <SyncIcon fontSize="small" color="warning" />,
  error: <ErrorIcon fontSize="small" color="error" />,
};

function formatElapsed(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1000) return `${Math.round(n)} ms`;
  if (n < 60000) return `${(n / 1000).toFixed(1)} s`;
  const mins = Math.floor(n / 60000);
  const secs = Math.floor((n % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

function mapTenantItem(row, t) {
  if (row?.error) {
    return {
      status: "error",
      detail: row.error,
      mpUpdated: Number(row.mpUpdated || 0),
      profileUpdated: Number(row.profileUpdated || 0),
      scanned: 0,
      total: 0,
    };
  }
  const hasMore = Boolean(row?.hasMore);
  const scanned = Number(row?.nextOffset ?? row?.scanned ?? 0);
  const total = Number(row?.total || 0);
  const status = hasMore ? "partial" : "ok";
  const detail = hasMore
    ? t("platformPage.passengerFeesRecalcTenantPartial", {
        scanned,
        total,
        mp: Number(row.mpUpdated || 0),
        profile: Number(row.profileUpdated || 0),
      })
    : t("platformPage.passengerFeesRecalcTenantDone", {
        mp: Number(row.mpUpdated || 0),
        profile: Number(row.profileUpdated || 0),
        total: total || scanned,
      });
  return {
    status,
    detail,
    mpUpdated: Number(row.mpUpdated || 0),
    profileUpdated: Number(row.profileUpdated || 0),
    scanned,
    total,
  };
}

/**
 * Recalcula taxas gravadas em passagens_vendas para o período (MP + perfis).
 */
export default function PassengerFeesRecalcButton({ from, to, dominioId, onComplete, size = "small", variant = "outlined" }) {
  const { t } = useTranslation("app");
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!running) return undefined;
    const id = window.setInterval(() => setTick((n) => n + 1), 500);
    return () => window.clearInterval(id);
  }, [running]);

  const run = useCallback(async () => {
    setRunning(true);
    setError("");
    setResult(null);

    const startedAt = Date.now();
    let offsets = {};
    let tenantOffset = 0;
    let batch = 0;
    let totalUpdated = 0;
    let totalMpUpdated = 0;
    let totalProfileUpdated = 0;
    let totalErrors = 0;
    const tenantMap = new Map();

    setProgress({
      startedAt,
      batch: 0,
      activity: t("platformPage.passengerFeesRecalcPhaseStart"),
      totalUpdated: 0,
      totalMpUpdated: 0,
      totalProfileUpdated: 0,
      totalErrors: 0,
      tenantsTotal: dominioId ? 1 : null,
      tenantOffset: 0,
      items: [],
    });

    try {
      for (;;) {
        batch += 1;
        setProgress((prev) => ({
          ...prev,
          batch,
          activity: t("platformPage.passengerFeesRecalcPhaseBatch", { batch }),
        }));

        const data = await postPlatformPassengerFeesBackfill({
          from,
          to,
          dominioId: dominioId || undefined,
          offsets,
          tenantOffset,
          forceMpBackfill: true,
        });

        totalUpdated += Number(data?.updated || 0);
        totalMpUpdated += Number(data?.mpUpdated || 0);
        totalProfileUpdated += Number(data?.profileUpdated || 0);
        totalErrors += Number(data?.errors || 0);
        offsets = data?.offsets && typeof data.offsets === "object" ? data.offsets : {};
        tenantOffset = Number(data?.tenantOffset ?? tenantOffset);

        for (const row of data?.tenants || []) {
          const key = String(row.dominioId);
          const mapped = mapTenantItem(row, t);
          const existing = tenantMap.get(key);
          tenantMap.set(key, {
            dominioId: row.dominioId,
            dominio: row.dominio || existing?.dominio || key,
            status: mapped.status,
            detail: mapped.detail,
            mpUpdated: (existing?.mpUpdated || 0) + mapped.mpUpdated,
            profileUpdated: (existing?.profileUpdated || 0) + mapped.profileUpdated,
            scanned: mapped.scanned,
            total: mapped.total,
            error: row.error || null,
          });
        }

        const items = [...tenantMap.values()];
        const runningTenant = items.find((it) => it.status === "partial") || items[items.length - 1];
        const activity = runningTenant
          ? t("platformPage.passengerFeesRecalcPhaseTenant", {
              workspace: runningTenant.dominio,
              mp: totalMpUpdated,
              profile: totalProfileUpdated,
            })
          : t("platformPage.passengerFeesRecalcPhaseBatch", { batch });

        setProgress({
          startedAt,
          batch,
          activity,
          totalUpdated,
          totalMpUpdated,
          totalProfileUpdated,
          totalErrors,
          tenantsTotal: Number(data?.tenantsTotal ?? items.length) || null,
          tenantOffset: Number(data?.tenantIndex ?? 0) + 1,
          items,
        });

        if (!data?.hasMore || batch >= 80) break;
      }

      const items = [...tenantMap.values()];
      const durationMs = Date.now() - startedAt;
      setProgress(null);
      setResult({
        totalUpdated,
        totalMpUpdated,
        totalProfileUpdated,
        totalErrors,
        batches: batch,
        durationMs,
        items,
        partial: items.some((it) => it.status === "partial"),
      });
      onComplete?.();
    } catch (e) {
      setProgress(null);
      setError(e?.response?.data?.message || e?.message || t("platformPage.passengerFeesRecalcError"));
    } finally {
      setRunning(false);
    }
  }, [dominioId, from, onComplete, t, to]);

  const progressPct = useMemo(() => {
    if (!progress?.tenantsTotal) return null;
    const done = Math.min(progress.tenantOffset || 0, progress.tenantsTotal);
    return Math.round((done / progress.tenantsTotal) * 100);
  }, [progress]);

  const elapsedLabel = useMemo(() => {
    if (!progress?.startedAt) return null;
    void tick;
    return formatElapsed(Date.now() - progress.startedAt);
  }, [progress, tick]);

  const renderItems = (items, keyPrefix) => (
    <Paper variant="outlined" sx={{ maxHeight: 240, overflow: "auto" }}>
      <List dense disablePadding>
        {(items || []).map((item) => (
          <ListItem
            key={`${keyPrefix}-${item.dominioId}`}
            alignItems="flex-start"
            sx={{ py: 0.75, borderBottom: 1, borderColor: "divider", "&:last-child": { borderBottom: 0 } }}
          >
            <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
              {STATUS_ICON[item.status] || STATUS_ICON.pending}
            </ListItemIcon>
            <ListItemText
              primary={
                <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap flexWrap="wrap">
                  <Typography variant="body2">{item.dominio}</Typography>
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {item.mpUpdated > 0 ? (
                      <Chip size="small" label={`MP ${item.mpUpdated}`} variant="outlined" sx={{ height: 22 }} />
                    ) : null}
                    {item.profileUpdated > 0 ? (
                      <Chip size="small" label={`${t("platformPage.passengerFeesRecalcProfileShort")} ${item.profileUpdated}`} variant="outlined" sx={{ height: 22 }} />
                    ) : null}
                  </Stack>
                </Stack>
              }
              secondary={
                <Typography variant="caption" color={item.status === "error" ? "error" : "text.secondary"} display="block">
                  {item.detail}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );

  return (
    <Stack spacing={1.5}>
      <Button
        size={size}
        variant={variant}
        startIcon={running ? <CircularProgress size={16} /> : <CalculateOutlinedIcon />}
        onClick={run}
        disabled={running || !from || !to}
      >
        {running ? t("platformPage.passengerFeesRecalcRunning") : t("platformPage.passengerFeesRecalc")}
      </Button>

      {running && progress ? (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }} useFlexGap flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              {t("platformPage.passengerFeesRecalcProgressSummary", {
                updated: progress.totalUpdated,
                mp: progress.totalMpUpdated,
                profile: progress.totalProfileUpdated,
                errors: progress.totalErrors,
              })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {elapsedLabel}
              {progressPct != null ? ` · ${progressPct}%` : ""}
            </Typography>
          </Stack>
          <LinearProgress
            variant={progressPct != null ? "determinate" : "indeterminate"}
            value={progressPct ?? undefined}
            sx={{ mb: 0.75 }}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {progress.activity}
          </Typography>
          {progress.items?.length ? renderItems(progress.items, "prog") : null}
        </Box>
      ) : null}

      {result && !running ? (
        <Alert
          severity={result.partial || result.totalErrors > 0 ? "warning" : "success"}
          sx={{ py: 0.75 }}
          onClose={() => setResult(null)}
        >
          <Typography variant="body2" sx={{ mb: result.items?.length ? 1 : 0 }}>
            {t("platformPage.passengerFeesRecalcDoneDetail", {
              updated: result.totalUpdated,
              mp: result.totalMpUpdated,
              profile: result.totalProfileUpdated,
              errors: result.totalErrors,
              batches: result.batches,
              duration: (result.durationMs / 1000).toFixed(1),
            })}
          </Typography>
          {result.items?.length ? renderItems(result.items, "done") : null}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ py: 0.25 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
    </Stack>
  );
}
